import React, {useEffect} from 'react';
import { AdvisorFields } from './types';
import LazySection from '../../react-only-components/LazySection';
import { documentService } from '../../utils/window.utils.js';
import './styles.scss';

const Advisor = (sitecoreFields: AdvisorFields): JSX.Element => {
  const featuredImage = sitecoreFields?.fields?.['Advisor Image']?.value ?? { src: '//localhost:8085/images/Advisor.jpg' };
  const proceedButtonText = sitecoreFields?.fields?.['Proceed Button Text']?.value || 'Proceed';
  const scriptUrl = sitecoreFields?.fields?.['Script Url']?.value || '//stage.excentos.com/Corporate_agriculture/DE/app_corporate-Tractor-Guide/en_GB/loadadvisor?theme=default';

  const selectors = {
    advisorId: 'xc_application',
    advisorScriptId: 'advisor-sript',
    paginationBarId: 'advisor-pagination-bar',
    paginationCurrentSlideId: 'advisor-pagination-counter',
    paginationBackButtonId: 'advisor-pagination-back-button',
    loadingCircle: '#xc_application .sk-fading-circle',
    radioButtons: '#xc_wizard_container_1 .xc_tile__footer-wrapper',
    whatAreYouLookingForRadio: '#xc_wizard_container_1 > div.ycc1e3-0.hdbgso.xc_sized-wrapper > div.sc-13bvdeq-0.lmyPrR.xc_wizard-body > section.xc_slide-container.xc_slide-container--current > div > div.xc_stage-wrapper__body > div.xc_stage-wrapper__children > div.xc_question-wrapper > div.xc_question-body-wrapper > div > div',
    wrapper: '#xc_wizard_container_1 > div.ycc1e3-0.hdbgso.xc_sized-wrapper',
    slides: '#xc_wizard_container_1 > div.ycc1e3-0.hdbgso.xc_sized-wrapper > .xc_wizard-body .xc_slide-container',
    backButton: '#xc_wizard_container_1 .xc_nav-button.xc_previous-button',
    nextButton: '#xc_wizard_container_1 .xc_nav-button.xc_next-button',
    powerRangeYesButton: '#xc_wizard_container_1 > div.ycc1e3-0.hdbgso.xc_sized-wrapper > div.sc-13bvdeq-0.lmyPrR.xc_wizard-body > section.xc_slide-container.xc_slide-container--current > div > div.xc_stage-wrapper__body > div.xc_stage-wrapper__children > div:nth-child(1) > div.xc_question-body-wrapper > div:nth-child(1) > div',
    powerRangeNewOptionsButton: '#xc_wizard_container_1 > div.ycc1e3-0.hdbgso.xc_sized-wrapper > div.sc-13bvdeq-0.lmyPrR.xc_wizard-body > section.xc_slide-container.xc_slide-container--current > div > div.xc_stage-wrapper__body > div.xc_stage-wrapper__children > div.xc_question-wrapper > div.xc_question-body-wrapper > div:nth-child(2) > div',
    powerRangeRadioButtons: '#xc_wizard_container_1 > div.ycc1e3-0.hdbgso.xc_sized-wrapper > div.sc-13bvdeq-0.lmyPrR.xc_wizard-body > section.xc_slide-container.xc_slide-container--current > div > div.xc_stage-wrapper__body > div.xc_stage-wrapper__children > div:nth-child(2) > div.xc_question-body-wrapper > div',
    powerRangeRadioButtonsWrapper: `#xc_wizard_container_1 [data-id='xcAjaxClient.wizard.power_range.facet_51.pwr_range']`,
    restartButton: '#xc_wizard-header > div > div.gjo8co-0.d2goba-0.igaRF.xc_header-button.xc_restart-button',
    restartButtonId: 'advisor-restart',
    lastSlide: '#xc_wizard_container_1 > div.ycc1e3-0.hdbgso.xc_sized-wrapper > div.sc-13bvdeq-0.RInbK.xc_wizard-body > div.xc_stage-wrapper.xc_endgame-flex-container__wrapper.xc_stage-wrapper--no-padding > div',
    sliderWrapper: '#xc_wizard_container_1 > div.ycc1e3-0.hdbgso.xc_sized-wrapper > div.sc-13bvdeq-0.RInbK.xc_wizard-body'
  }

  useEffect(() => {
    if (typeof window !== 'undefined' && 'customElements' in window) {
      import('@patternfly/pfe-icon-onhighway/dist/pfe-icon.js');
    }
  }, []);


  let currentSlide = 0;
  const slideCount = 5;
  let radioButtons: NodeList | undefined = undefined;

  const loadScript = (src: string) =>
    new Promise((resolve, reject) => {
      let tag = documentService()?.createElement('script');
      tag.async = false;
      tag.id = selectors.advisorScriptId;
      tag.src = src;
      tag.onload = resolve;
      tag.onerror = reject;
      documentService()?.getElementsByTagName('body')[0].appendChild(tag);
    });

  const waitAdvisorToLoad = (): Promise<boolean> =>
    new Promise((resolve) => {
      const observer = new MutationObserver(() => {
        if (documentService()?.querySelector(selectors.loadingCircle) === null) {
          observer.disconnect();
          resolve(true);
        }
      });
      const config = { attributes: true, childList: true, subtree: true };
      const advisorEle = documentService()?.getElementById(selectors.advisorId);
      if (advisorEle) {
        observer.observe(advisorEle, config);
      }
    });

  const updateSlideAfterClick = async () => {
    await waitSliderToUpdate();
    updateSlide();
  }

  const initializeSlide = () => {
    if (currentSlide < 6) {
      appendPagination();
      bindBackButton();
    }
    else {
      bindBackButton();
    }
    updateSlide();
  }

  const loadAdvisor = async () => {
    await loadScript(scriptUrl);
    await waitAdvisorToLoad();
    initializeSlide();
  }

  const getCurrentSlideNumber = () => {
    let number = 0; //last slide is not in slider
    const slides = documentService()?.querySelectorAll(selectors.slides)
    slides.forEach((slide, index) => {
      if (slide.classList.contains('xc_slide-container--current')) {
        number = index + 1;
      }
    });
    return slides.length > 0 ? number : 6;
  }

  const getCurrentSlide = (): HTMLElement => {
    let currentSlide;
    const slides = documentService()?.querySelectorAll(selectors.slides)
    slides.forEach((slide, index) => {
      if (slide.classList.contains('xc_slide-container--current')) {
        currentSlide = slide;
      }
    });
    return slides.length > 0 ? currentSlide : documentService()?.querySelector(selectors.lastSlide);
  }

  const waitSliderToUpdate = (): Promise<void> =>
    new Promise((resolve) => {
      const lastSlide = getCurrentSlide();
      const slidesObserver = new MutationObserver(() => {
        if (getCurrentSlide() !== lastSlide)
          resolve();
        if (documentService()?.querySelectorAll(selectors.slides).length < 1)
          resolve();
      });
      const config = { attributes: true, childList: true, subtree: true };
      slidesObserver.observe(documentService()?.querySelector(selectors.slides)?.parentElement, config);
    });

  const waitPowerRangeSlideToUpdate = (): Promise<void> =>
    new Promise((resolve) => {
      const slideObserver = new MutationObserver(() => {
        if (documentService()?.querySelector(selectors.powerRangeRadioButtonsWrapper))
          resolve();
      });
      const config = { attributes: true, childList: true, subtree: true };
      slideObserver.observe(getCurrentSlide(), config);
    });

  const nextButtonClick = () => {
    let nextButton: HTMLElement = documentService()?.querySelector(selectors.nextButton);
    nextButton?.click();
    updateSlideAfterClick();
  }

  const backButtonClick = () => {
    const backButton: HTMLElement = documentService()?.querySelector(selectors.backButton);
    backButton?.click();
    updateSlideAfterClick();
  }

  const bindBackButton = () => {
    documentService()?.getElementById(selectors.paginationBackButtonId).addEventListener("click", async () => {
      backButtonClick();
    });
  }

  const bindClickOnRadioButtons = () => {
    radioButtons = documentService()?.querySelectorAll(selectors.radioButtons);
    (radioButtons || []).forEach(button => {
      button.addEventListener('click', () => {
        nextButtonClick();
      });
    })
  }

  const bindClickOnWhatAreYouLookingRadioButtons = () => {
    radioButtons = documentService()?.querySelectorAll(selectors.whatAreYouLookingForRadio);
    (radioButtons || []).forEach(button => {
      button.addEventListener('click', () => {
        nextButtonClick();
      });
    })
  }

  const bindClickOnPowerRangeRadioButtons = () => {
    radioButtons = documentService()?.querySelectorAll(selectors.powerRangeRadioButtons);
    (radioButtons || []).forEach(button => {
      button.firstChild.addEventListener('click', () => {
        nextButtonClick();
      });
    })
  }

  const appendImage = (): void => {
    let image: HTMLElement = documentService()?.createElement('div');
    image.className = 'xc_featured-image-wrapper';
    image.innerHTML = `<img loading="lazy" class="xc_featured-image" src="${featuredImage && featuredImage.src}" alt="${featuredImage && featuredImage.alt}"/>`;
    let currentSlideContainer = getCurrentSlide()?.firstChild?.firstChild;
    currentSlideContainer?.insertBefore(image, currentSlideContainer.firstChild);
  }

  const appendProceedButton = () => {
    let proceedButton: HTMLElement = documentService()?.createElement('button');
    proceedButton.className = 'xc_proceed-button';
    proceedButton.innerHTML = `${proceedButtonText}`;
    let currentSlideContainer = getCurrentSlide()?.firstChild?.firstChild?.lastChild;
    currentSlideContainer?.appendChild(proceedButton);
    proceedButton.addEventListener('click', () => {
      nextButtonClick();
    })
  }

  const bindYesButton = () => {
    const yesButton = documentService()?.querySelector(selectors.powerRangeYesButton);
    yesButton.addEventListener('click', async () => {
      await waitPowerRangeSlideToUpdate();
      bindClickOnPowerRangeRadioButtons();
    })
  }

  const bindNewOptionsButton = () => {
    documentService()?.querySelector(selectors.powerRangeNewOptionsButton).addEventListener('click', () => {
      nextButtonClick();
    })
  }

  const appendPagination = () => {
    currentSlide = getCurrentSlideNumber();
    let pagination: HTMLElement = documentService()?.createElement('div');
    pagination.className = 'xc_pagination';
    pagination.innerHTML = `
            <div id="${selectors.paginationBackButtonId}" class="xc_pagination-back-button ${currentSlide < 2 ? "xc_pagination-back-button--hidden" : ""}">
                <pfe-icon icon="dcx-arrow-i-os-backward" size="16" ></pfe-icon>
            </div>
            <div class="xc_pagination-bar">
                <div id="${selectors.paginationBarId}" class="xc_pagination-bar-line xc_pagination-bar-line-${currentSlide}">
                </div>
            </div>
            <div class="xc_pagination-counter">
                <span id="${selectors.paginationCurrentSlideId}">${currentSlide}</span>/${slideCount}
            </div>
        `;
    const wrapper: HTMLElement = documentService()?.querySelector(selectors.wrapper);
    wrapper.appendChild(pagination);
  }

  const removePagination = () => {
    documentService()?.getElementById(selectors.paginationBarId)?.parentElement?.parentElement?.remove();
  }

  const addResetButton = () => {
    if (!documentService()?.getElementById(selectors.restartButtonId)) {
      const restartButtonCopy: HTMLElement = documentService()?.querySelector(selectors.restartButton);
      let restartButton: HTMLElement = documentService()?.createElement('button');
      restartButton.className = 'xc_restart-button';
      restartButton.innerHTML = `<pfe-icon icon="dcx-arrow-i-os-backward" size="18" style="padding-bottom:2px"></pfe-icon> Restart`;
      let currentSlideContainer = documentService()?.querySelector(selectors.sliderWrapper);
      currentSlideContainer && currentSlideContainer.appendChild(restartButton);
      restartButton.addEventListener('click', () => {
        restartButtonCopy.click();
      })
    }
  }

  const updatePagination = () => {
    currentSlide = getCurrentSlideNumber();
    if (documentService()) {
      documentService().getElementById(selectors.paginationBackButtonId).className = `xc_pagination-back-button ${currentSlide < 2 ? "xc_pagination-back-button--hidden" : ""}`;
      documentService().getElementById(selectors.paginationBarId).className = `xc_pagination-bar-line xc_pagination-bar-line-${currentSlide}`;
      documentService().getElementById(selectors.paginationCurrentSlideId).innerHTML = currentSlide.toString();
    }
  }

  const updateSlide = () => {
    const currentSlide = getCurrentSlideNumber();
    if (currentSlide < 6) {
      updatePagination();
      appendImage();
    }
    if (currentSlide == 1 || currentSlide == 2) {
      bindClickOnRadioButtons();
    }
    if (currentSlide == 3) {
      appendProceedButton();
    }
    if (currentSlide == 4) {
      bindYesButton();
      bindNewOptionsButton();
      if (documentService()?.querySelector(selectors.powerRangeRadioButtonsWrapper)) {
        bindClickOnPowerRangeRadioButtons();
      }
    }
    if (currentSlide == 5) {
      bindClickOnWhatAreYouLookingRadioButtons();
    }
    if (currentSlide == 6) {
      removePagination();
      addResetButton();
    }
  }

  function LoadAdvisor() {
    loadAdvisor();

    return (
      <div id="xc_application" className="advisor">
        <div id="xc_application_pane"></div>
        <div id="xc_loader"></div>
      </div>
    );
  }

  return (
    <LazySection>
      <div className="advisor-wrapper">
        <LoadAdvisor />
      </div>
    </LazySection>
  );
}

export default Advisor;
